/**
 * Polished sign-in / sign-up / confirm / forgot-password / reset-confirm flow.
 *
 * Five modes, all hosted on a single page with mode driven by a query
 * parameter (`?mode=sign-up` etc.) so deep links and email magic-links work.
 *
 * Validation: zod + react-hook-form. Cognito's password policy is set in
 * AuthStack (12+ chars, mixed case, digit, symbol, leaked-password
 * protection on); this form enforces the same at the client tier so we
 * surface errors before a Cognito round-trip.
 */
import { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { analytics } from '@/lib/analytics';
import { api, ApiHttpError } from '@/lib/api';
import { getTurnstileToken, turnstileEnabled } from '@/lib/turnstile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';

type Mode = 'sign-in' | 'sign-up' | 'confirm' | 'forgot' | 'reset';

const passwordSchema = z
  .string()
  .min(12, 'At least 12 characters')
  .regex(/[a-z]/, 'At least one lowercase letter')
  .regex(/[A-Z]/, 'At least one uppercase letter')
  .regex(/[0-9]/, 'At least one digit')
  .regex(/[^a-zA-Z0-9]/, 'At least one symbol');

const signInSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password required'),
});
const signUpSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: passwordSchema,
});
const confirmSchema = z.object({
  email: z.string().email('Enter a valid email'),
  code: z.string().regex(/^\d{6}$/, 'Six-digit code'),
});
const forgotSchema = z.object({ email: z.string().email('Enter a valid email') });
const resetSchema = z.object({
  email: z.string().email('Enter a valid email'),
  code: z.string().regex(/^\d{6}$/, 'Six-digit code'),
  newPassword: passwordSchema,
});

/**
 * Validate a post-auth redirect target. Only same-origin relative paths are
 * allowed: must start with a single '/' (so no absolute URLs / schemes) and
 * must not be protocol-relative ('//evil.com') or the backslash variant
 * ('/\evil.com') some browsers normalize to '//'.
 */
function isSafeNextPath(value: string | null | undefined): value is string {
  if (!value) return false;
  return value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\');
}

export function AuthPage() {
  const auth = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const mode = (params.get('mode') as Mode | null) ?? 'sign-in';
  // Plan intent forwarded from /pricing → /auth?plan=…&action=… ; we carry it
  // through sign-in / confirm so /onboarding can resume the trial-or-checkout
  // step instead of dropping the user on a bare dashboard.
  const planParam = params.get('plan');
  const actionParam = params.get('action');
  // Explicit redirect target, e.g. /auth?next=%2Finvite%3Ftoken%3D… (used by
  // InvitePage and the api client's expired-session redirect).
  const nextParam = params.get('next');
  // AuthGuard redirects here with the blocked location in state.from.
  const stateFrom = (location.state as { from?: { pathname?: string; search?: string } } | null)
    ?.from;
  // Where to route AFTER auth resolves, in priority order:
  //   1. ?next=<path> — validated same-origin path (deep links, invites,
  //      session-expiry redirects).
  //   2. location.state.from — where AuthGuard bounced the user from.
  //   3. Plan/action intent (from /pricing) → /onboarding resumes the
  //      trial-or-checkout step instead of dropping the user on a bare
  //      dashboard.
  //   4. /dashboard. Sending every returning user through /onboarding caused
  //      a 2-3s "Add your first domain" flash on every login while /me was
  //      loading.
  const postAuthHref = useMemo(() => {
    if (isSafeNextPath(nextParam)) return nextParam;
    const fromHref = stateFrom?.pathname
      ? `${stateFrom.pathname}${stateFrom.search ?? ''}`
      : null;
    if (isSafeNextPath(fromHref)) return fromHref;
    const hasIntent = !!planParam && !!actionParam && actionParam !== 'free';
    if (!hasIntent) return '/dashboard';
    const q = new URLSearchParams();
    q.set('plan', planParam);
    q.set('action', actionParam);
    return `/onboarding?${q.toString()}`;
  }, [nextParam, stateFrom, planParam, actionParam]);
  const setMode = (m: Mode) => {
    const next = new URLSearchParams(params);
    next.set('mode', m);
    setParams(next);
  };

  // In-memory stash of the credentials a user just typed during sign-up. Used
  // by ConfirmForm to auto-sign-in after the email-confirmation step. Lives
  // for the lifetime of the AuthPage mount only — never persisted.
  const [pendingCreds, setPendingCreds] = useState<{ email: string; password: string } | null>(
    null,
  );
  // Pre-fill email from the Forgot screen into the Reset screen so the user
  // doesn't think they're being asked for it twice. Cleared after a successful
  // reset.
  const [resetEmail, setResetEmail] = useState<string>('');

  if (auth.user) return <Navigate to={postAuthHref} replace />;

  return (
    <>
      <Helmet>
        <title>{titleFor(mode)} — InboxGuard</title>
        <meta name="description" content="Sign in to InboxGuard to monitor email deliverability." />
      </Helmet>

      <div className="hero-grad min-h-screen">
        <PublicHeader />

        <main className="flex items-center justify-center px-4 py-16 sm:py-24">
          <Card className="card-elev w-full max-w-md animate-fade-in-up">
            <CardHeader className="space-y-3 pb-6 pt-8">
              <CardTitle className="text-2xl tracking-tight">{headingFor(mode)}</CardTitle>
              <CardDescription className="text-balance">{descriptionFor(mode)}</CardDescription>
            </CardHeader>
            <CardContent className="pb-8">
              {mode === 'sign-in' && (
                <SignInForm
                  onConfirm={() => setMode('confirm')}
                  onForgot={() => setMode('forgot')}
                  redirectTo={postAuthHref}
                />
              )}
              {mode === 'sign-up' && (
                <SignUpForm
                  onConfirm={(creds) => {
                    setPendingCreds(creds);
                    setMode('confirm');
                  }}
                />
              )}
              {mode === 'confirm' && (
                <ConfirmForm
                  pendingCreds={pendingCreds}
                  onSignedIn={() => {
                    setPendingCreds(null);
                    nav(postAuthHref);
                  }}
                />
              )}
              {mode === 'forgot' && (
                <ForgotForm
                  onSent={(email) => {
                    setResetEmail(email);
                    setMode('reset');
                  }}
                />
              )}
              {mode === 'reset' && (
                <ResetForm
                  initialEmail={resetEmail}
                  onComplete={() => {
                    setResetEmail('');
                    setMode('sign-in');
                  }}
                />
              )}

              <div className="mt-8 text-center text-sm">
                {mode === 'sign-in' && (
                  <>
                    Don't have an account?{' '}
                    <button onClick={() => setMode('sign-up')} className="text-primary underline-offset-4 hover:underline">
                      Sign up
                    </button>
                  </>
                )}
                {mode === 'sign-up' && (
                  <>
                    Already have an account?{' '}
                    <button onClick={() => setMode('sign-in')} className="text-primary underline-offset-4 hover:underline">
                      Sign in
                    </button>
                  </>
                )}
                {(mode === 'confirm' || mode === 'forgot' || mode === 'reset') && (
                  <button onClick={() => setMode('sign-in')} className="inline-flex items-center text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="mr-1 h-3 w-3" /> Back to sign in
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </main>
        <PublicFooter />
      </div>
    </>
  );
}

// --- forms ----------------------------------------------------------------

function SignInForm({
  onConfirm,
  onForgot,
  redirectTo,
}: {
  onConfirm: () => void;
  onForgot: () => void;
  redirectTo: string;
}) {
  const auth = useAuth();
  const nav = useNavigate();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<z.infer<typeof signInSchema>>({
    resolver: zodResolver(signInSchema),
  });

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit(async (data) => {
        try {
          await auth.signIn(data.email, data.password);
          toast.success('Welcome back.');
          nav(redirectTo);
        } catch (err) {
          const msg = (err as Error).message ?? 'Sign-in failed';
          if (/UserNotConfirmedException/i.test(msg)) {
            toast.message('Please confirm your email first.');
            onConfirm();
          } else {
            toast.error(msg);
          }
        }
      })}
    >
      <Field id="email" label="Email" error={errors.email?.message}>
        <Input id="email" type="email" autoComplete="email" {...register('email')} />
      </Field>
      <Field id="password" label="Password" error={errors.password?.message}>
        <Input id="password" type="password" autoComplete="current-password" {...register('password')} />
      </Field>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
      </Button>
      <div className="text-center">
        <button type="button" onClick={onForgot} className="text-sm text-muted-foreground hover:text-foreground">
          Forgot password?
        </button>
      </div>
    </form>
  );
}

function SignUpForm({ onConfirm }: { onConfirm: (creds: { email: string; password: string }) => void }) {
  const auth = useAuth();
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema),
  });
  const password = watch('password') ?? '';

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit(async (data) => {
        try {
          analytics.trackEvent('signup_started');
          // Bot precheck — runs the invisible Turnstile challenge and verifies
          // server-side BEFORE we hit Cognito's signUp. If Turnstile isn't
          // configured (no site key in env), getTurnstileToken returns null
          // and the server-side verifier fails-open, so this reduces to a
          // ~10ms ping in dev and full bot protection in prod.
          if (turnstileEnabled()) {
            const token = await getTurnstileToken('signup').catch(() => null);
            try {
              await api.post('/verify-bot', { token, action: 'signup' }, { noAuth: true });
            } catch (e) {
              if (e instanceof ApiHttpError && e.status === 403) {
                toast.error('Bot verification failed. Please refresh and try again.');
                return;
              }
              throw e;
            }
          }
          const out = await auth.signUp(data.email, data.password);
          if (out.requiresConfirmation) {
            toast.message('Check your email for a six-digit confirmation code.');
            // Stash creds in parent so ConfirmForm can auto-sign-in after the
            // user enters the code — saves them from re-typing the password
            // they just typed two seconds ago.
            onConfirm({ email: data.email, password: data.password });
          }
        } catch (err) {
          toast.error((err as Error).message ?? 'Sign-up failed');
        }
      })}
    >
      <Field id="email" label="Email" error={errors.email?.message}>
        <Input id="email" type="email" autoComplete="email" {...register('email')} />
      </Field>
      <Field id="password" label="Password" error={errors.password?.message}>
        <Input id="password" type="password" autoComplete="new-password" {...register('password')} />
        <PasswordHints value={password} />
      </Field>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create account'}
      </Button>
    </form>
  );
}

function ConfirmForm({
  pendingCreds,
  onSignedIn,
}: {
  pendingCreds: { email: string; password: string } | null;
  onSignedIn: () => void;
}) {
  const auth = useAuth();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<z.infer<typeof confirmSchema>>({
    resolver: zodResolver(confirmSchema),
    // Pre-fill email from the sign-up step so the user only has to type the code.
    defaultValues: pendingCreds ? { email: pendingCreds.email } : undefined,
  });

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit(async (data) => {
        try {
          await auth.confirmSignUp(data.email, data.code);
          // Fires both `sign_up` (GA4 recommended → conversion funnel) and
          // legacy `signup_completed` (existing dashboards).
          analytics.trackSignUp('email');
          // If we still have the password the user typed during sign-up,
          // auto-sign them in so they land on /onboarding ready to scan a
          // domain. Otherwise, fall back to the manual sign-in screen.
          if (pendingCreds && pendingCreds.email === data.email) {
            try {
              await auth.signIn(pendingCreds.email, pendingCreds.password);
              toast.success('Welcome to InboxGuard.');
              onSignedIn();
              return;
            } catch (signInErr) {
              toast.message('Email confirmed. Please sign in to continue.');
              throw signInErr;
            }
          }
          toast.success('Email confirmed. Please sign in.');
          onSignedIn();
        } catch (err) {
          toast.error((err as Error).message ?? 'Confirmation failed');
        }
      })}
    >
      <Field id="email" label="Email" error={errors.email?.message}>
        <Input id="email" type="email" autoComplete="email" {...register('email')} />
      </Field>
      <Field id="code" label="Confirmation code" error={errors.code?.message}>
        <Input id="code" inputMode="numeric" pattern="[0-9]*" maxLength={6} {...register('code')} />
      </Field>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
      </Button>
    </form>
  );
}

function ForgotForm({ onSent }: { onSent: (email: string) => void }) {
  const auth = useAuth();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<z.infer<typeof forgotSchema>>({
    resolver: zodResolver(forgotSchema),
  });

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit(async (data) => {
        try {
          await auth.resetPassword(data.email);
          toast.success(`Code sent to ${data.email}. Check your inbox.`);
          onSent(data.email);
        } catch (err) {
          toast.error((err as Error).message ?? 'Could not start reset');
        }
      })}
    >
      <Field id="email" label="Email" error={errors.email?.message}>
        <Input id="email" type="email" autoComplete="email" {...register('email')} />
      </Field>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset code'}
      </Button>
    </form>
  );
}

function ResetForm({
  onComplete,
  initialEmail = '',
}: {
  onComplete: () => void;
  initialEmail?: string;
}) {
  const auth = useAuth();
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: { email: initialEmail },
  });
  const newPassword = watch('newPassword') ?? '';

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit(async (data) => {
        try {
          await auth.confirmResetPassword(data.email, data.code, data.newPassword);
          toast.success('Password reset. Sign in with your new password.');
          onComplete();
        } catch (err) {
          toast.error((err as Error).message ?? 'Reset failed');
        }
      })}
    >
      {initialEmail && (
        <p className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
          We sent a 6-digit code to <strong className="text-foreground">{initialEmail}</strong>. Enter
          it below with your new password.
        </p>
      )}
      <Field id="email" label="Email" error={errors.email?.message}>
        <Input id="email" type="email" autoComplete="email" readOnly={!!initialEmail} {...register('email')} />
      </Field>
      <Field id="code" label="Reset code" error={errors.code?.message}>
        <Input id="code" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus={!!initialEmail} {...register('code')} />
      </Field>
      <Field id="newPassword" label="New password" error={errors.newPassword?.message}>
        <Input id="newPassword" type="password" autoComplete="new-password" {...register('newPassword')} />
        <PasswordHints value={newPassword} />
      </Field>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset password'}
      </Button>
    </form>
  );
}

// --- helpers --------------------------------------------------------------

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function PasswordHints({ value }: { value: string }) {
  const checks = useMemo(
    () => [
      { label: '12+ characters', ok: value.length >= 12 },
      { label: 'lowercase', ok: /[a-z]/.test(value) },
      { label: 'uppercase', ok: /[A-Z]/.test(value) },
      { label: 'digit', ok: /[0-9]/.test(value) },
      { label: 'symbol', ok: /[^a-zA-Z0-9]/.test(value) },
    ],
    [value],
  );
  return (
    <ul className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
      {checks.map((c) => (
        <li key={c.label} className={c.ok ? 'text-success' : 'text-muted-foreground'}>
          {c.ok ? '✓' : '○'} {c.label}
        </li>
      ))}
    </ul>
  );
}

function titleFor(mode: Mode): string {
  return {
    'sign-in': 'Sign in',
    'sign-up': 'Create account',
    confirm: 'Confirm email',
    forgot: 'Reset password',
    reset: 'Set new password',
  }[mode];
}
function headingFor(mode: Mode): string {
  return titleFor(mode);
}
function descriptionFor(mode: Mode): string {
  return {
    'sign-in': 'Welcome back. Sign in to monitor your domains.',
    'sign-up': 'Start a free trial — no credit card needed.',
    confirm: 'Enter the six-digit code we just emailed you.',
    forgot: 'We\'ll email you a code to reset your password.',
    reset: 'Pick a new password and sign back in.',
  }[mode];
}

// Default export so the router can lazy-load it later if we want.
export default AuthPage;
